import React from "react";

import { Jsme } from "jsme-react";
import {
  Col,
  Row,
  Form,
  Button,
  Table,
  Spinner,
  ButtonGroup,
  Card,
  Tab,
  Tabs,
} from "react-bootstrap";
import axios from "axios";
import initRDKit from "../layout/utils/initRDKit";
import { useSearchParams } from "react-router-dom";
import Base from "../layout/BaseTemplate";

function Bioisosteres() {
  let [searchParams] = useSearchParams();
  const [rdKit, setRdKit] = React.useState(null);
  const [smiles, setSmiles] = React.useState(searchParams.get("smiles") || "");
  const [smilestxt, setSmilestxt] = React.useState(searchParams.get("smiles") || "");
  const [task, setTask] = React.useState(searchParams.get("task") || null);
  const [results, setResults] = React.useState([]);

  const [molwtup, setMolwtup] = React.useState(true);
  const [molwtdown, setMolwtdown] = React.useState(true);
  const [logpup, setLogpup] = React.useState(true);
  const [logpdown, setLogpdown] = React.useState(true);
  const [sp3Bondup, setSp3Bondup] = React.useState(true);
  const [sp3Bonddown, setSp3Bonddown] = React.useState(true);

  const [spin, setSpin] = React.useState(false);
  const [isReady, setIsReady] = React.useState(false);
  const [count, setCount] = React.useState(1);
  const [molWt, setMolWt] = React.useState(null);
  const [logP, setLogP] = React.useState(null);
  const [rotableBonds, setRotableBonds] = React.useState(null);
  const [hba, setHBA] = React.useState(null);
  const [hbd, setHBD] = React.useState(null);

  function submitTask(overrideSmiles = null) {
    const smilesToUse = overrideSmiles ?? smilestxt;
    const bodyFormData = new FormData();
    bodyFormData.append("smiles", smilesToUse);
    bodyFormData.append("weightUp", molwtup);
    bodyFormData.append("weightDown", molwtdown);
    bodyFormData.append("logUp", logpup);
    bodyFormData.append("logDown", logpdown);
    bodyFormData.append("count", count);
    bodyFormData.append("sp3BondUp", sp3Bondup);
    bodyFormData.append("sp3BondDown", sp3Bonddown);

    setSpin(true);
    setIsReady(false);
    axios({
      url: "/bioisostere/transform",
      method: "post",
      data: bodyFormData,
      headers: { "Content-Type": "multipart/form-data" },
    }).then((response) => {
      setTask(response.data.task_id);
    });
  }

  function writesmiles(smiles, fromJSME = false, fromText = false) {
    setSmilestxt(smiles);
    setSmiles(smiles);
    if (fromJSME) {
      submitTask(smiles);
    }
  }

  function showspinner() {
    return spin ? <Spinner animation="grow" variant="success" /> : null;
  }

  function downloadResult(format) {
    axios({
      url: "/results/" + task + "." + format,
      method: "GET",
      responseType: "blob",
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "results." + format);
      link.click();
    });
  }

  function showResults(gen) {
    if (results.length === 0) return <tr>No results</tr>;
    const res = results[gen]["gen" + (gen + 1)];
    return res.map((result, index) => (
      <tr key={index}>
        <td>
          <div
            style={{
              position: "relative",
              width: "100px",
              height: "100px",
              display: "inline-block",
            }}
            draggable
            onDragStart={(e) => e.dataTransfer.setData("text/plain", result.smiles)}
            title="Drag to JSME"
          >
            <canvas
              id={`canvas-${result.smiles}`}
              width={100}
              height={100}
              style={{ pointerEvents: "auto" }}
            ></canvas>
            <Button
              variant="outline-secondary"
              size="sm"
              style={{
                position: "absolute",
                top: "4px",
                right: "4px",
                fontSize: "11px",
                padding: "2px 5px",
              }}
              onClick={() =>
                window.open(
                  `https://commons.docking.org/bioisosteres/transform?smiles=${result.smiles}`,
                  "_blank"
                )
              }
            >
              ↗
            </Button>
          </div>
        </td>
        <td>{result.smiles}</td>
        <td>{result.transform_name}</td>
        <td>{result.molW}</td>
        <td>{result.logP}</td>
        <td>{result.h_bond_acceptors}</td>
        <td>{result.h_bond_donors}</td>
      </tr>
    ));
  }

  React.useEffect(() => {
    if (!rdKit) {
      initRDKit().then(setRdKit);
    }
  }, []);

  React.useEffect(() => {
    if (!task) return;
    setSpin(true);
    setIsReady(false);
    function getProgress() {
      axios.get("/results/" + task).then((result) => {
        if (result.data.ready) {
          const data = result.data.results[0];
          setResults(data.results);
          setLogP(data.logP);
          setMolWt(data.molWt);
          setRotableBonds(data.rotatable_bonds);
          setHBA(data.h_bond_acceptors);
          setHBD(data.h_bond_donors);
          setSpin(false);
          setIsReady(true);
        } else {
          setTimeout(getProgress, 500);
        }
      });
    }
    getProgress();
  }, [task]);

  React.useEffect(() => {
    if (!rdKit || results.length === 0) return;
    results.forEach((group, genIndex) => {
      group["gen" + (genIndex + 1)].forEach((res) => {
        const canvas = document.getElementById("canvas-" + res.smiles);
        if (canvas) {
          const mol = rdKit.get_mol(res.smiles);
          mol.draw_to_canvas(canvas, 100, 100);
          mol.delete();
        }
      });
    });
  }, [results, rdKit]);

  return (
    <Base>
      <Row>
        <Col lg={6} md={6} sm={12} className="p-1">
          <Card className="p-2">
            <Form>
              <Row>
                <Col lg={9}>
                  <Jsme
                    src="/jsme/jsme.nocache.js"
                    height="20rem"
                    width="auto"
                    options="noautoez,newlook,nocanonize,multipart,zoom"
                    smiles={smiles}
                    onChange={(smi) => writesmiles(smi, true, false)}
                  />
                  <Form.Group>
                    <Form.Label><b>SMILES</b></Form.Label>
                    <Form.Control
                      type="text"
                      value={smilestxt}
                      onChange={(e) => writesmiles(e.target.value, false, true)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label><b>Transform Count</b></Form.Label>
                    <Form.Select onChange={(e) => setCount(e.target.value)} value={count}>
                      {[1, 2, 3].map((c) => <option key={c}>{c}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Check type="checkbox" label="Mwt ▲" checked={molwtup} onChange={(e) => setMolwtup(e.target.checked)} />
                  <Form.Check type="checkbox" label="Mwt ▼" checked={molwtdown} onChange={(e) => setMolwtdown(e.target.checked)} />
                  <Form.Check type="checkbox" label="logP ▲" checked={logpup} onChange={(e) => setLogpup(e.target.checked)} />
                  <Form.Check type="checkbox" label="logP ▼" checked={logpdown} onChange={(e) => setLogpdown(e.target.checked)} />
                  <Form.Check type="checkbox" label="sp3 ▲" checked={sp3Bondup} onChange={(e) => setSp3Bondup(e.target.checked)} />
                  <Form.Check type="checkbox" label="sp3 ▼" checked={sp3Bonddown} onChange={(e) => setSp3Bonddown(e.target.checked)} />
                </Col>
              </Row>
              <Button variant="success" onClick={() => submitTask()} className="mt-3">
                Transform
              </Button>
            </Form>
          </Card>
        </Col>
        <Col lg={6} md={6} sm={12} className="p-1 m-0">
          {results.length > 0 && (
            <div className="d-flex justify-content-between">
              <h4>Results</h4>
              <ButtonGroup>
                <Button variant="outline-success" onClick={() => downloadResult("txt")}>Download TXT</Button>
                <Button variant="outline-success" onClick={() => downloadResult("csv")}>Download CSV</Button>
                <Button variant="outline-success" onClick={() => downloadResult("json")}>Download JSON</Button>
              </ButtonGroup>
            </div>
          )}
          <div className="overflow-auto" style={{ maxHeight: "650px" }}>
            <Tabs>
              {results.map((_, index) => (
                <Tab eventKey={index} title={`Gen ${index + 1}`} key={index}>
                  <Table striped bordered hover size="md">
                    <thead>
                      <tr>
                        <th>&nbsp;</th>
                        <th>Smiles</th>
                        <th>Transform</th>
                        <th>MW</th>
                        <th>LogP</th>
                        <th>HBA</th>
                        <th>HBD</th>
                      </tr>
                    </thead>
                    <tbody>{isReady ? showResults(index) : showspinner()}</tbody>
                  </Table>
                </Tab>
              ))}
            </Tabs>
          </div>
        </Col>
      </Row>
    </Base>
  );
}

export default Bioisosteres;